import { useMemo, useState, useEffect } from 'react';
import type { OptionGroup } from '../types';
import { Field } from './Field';

type SelectProps = {
  label: string;
  group: OptionGroup;
  value: string | null;
  onChange: (next: string | null) => void;
  hint?: string | undefined;
};

const OTHER_SENTINEL = '__other__';

// A select that supports "Other → free text". The dropdown stays a normal native
// <select> (max accessibility, no popover code), and when the user picks "Other"
// we reveal an input below. Whatever they type becomes the canonical value.
export function Select({ label, group, value, onChange, hint }: SelectProps) {
  const knownValues = useMemo(() => new Set(group.options.map((o) => o.value)), [group.options]);

  // Local "other" string so the user can type freely without losing focus on every
  // keystroke; we propagate up via onChange.
  const [otherInput, setOtherInput] = useState<string>(
    value && !knownValues.has(value) ? value : '',
  );
  // "Other" mode is active when the current value isn't one of the canonical options
  // OR the user explicitly opens the other-input via the dropdown.
  const [otherOpen, setOtherOpen] = useState<boolean>(
    value !== null && !knownValues.has(value),
  );

  // If parent resets value (e.g. after a save), sync local state.
  useEffect(() => {
    if (value === null) {
      setOtherOpen(false);
      setOtherInput('');
    } else if (!knownValues.has(value)) {
      setOtherOpen(true);
      setOtherInput(value);
    } else {
      setOtherOpen(false);
    }
  }, [value, knownValues]);

  const selectValue = otherOpen ? OTHER_SENTINEL : (value ?? '');

  return (
    <Field label={label} hint={hint}>
      <select
        className="select__trigger"
        value={selectValue}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '') {
            setOtherOpen(false);
            onChange(null);
          } else if (v === OTHER_SENTINEL) {
            setOtherOpen(true);
            // Don't propagate yet — wait for them to type something real
            onChange(otherInput.trim() || null);
          } else {
            setOtherOpen(false);
            setOtherInput('');
            onChange(v);
          }
        }}
      >
        <option value="">Select…</option>
        {group.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
        {group.allow_other ? <option value={OTHER_SENTINEL}>Other…</option> : null}
      </select>
      {otherOpen ? (
        <input
          className="input input--small"
          placeholder="Type your answer"
          value={otherInput}
          maxLength={200}
          onChange={(e) => {
            setOtherInput(e.target.value);
            const trimmed = e.target.value.trim();
            onChange(trimmed === '' ? null : trimmed);
          }}
        />
      ) : null}
    </Field>
  );
}
